import { formatDate } from '../../../../utils/format';
import { StatusBadge } from '../../../../components/base/StatusBadge';
import type { Material, Season } from '../../../../types';

interface MaterialCardProps {
  material: Material;
  season?: Season;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function MaterialCard({ material, season, onView, onEdit, onDelete }: MaterialCardProps) {
  const isHtx = material.nguonGoc === 'HTX cấp/ứng';
  // HTX-issued material mirrors a stock-out already written to the cooperative's
  // ledger, so only they can unwind it — the backend refuses it either way.
  const canDelete = !isHtx;

  return (
    <div className="border-b border-slate-100 last:border-b-0 bg-white hover:bg-slate-50 transition-colors">
      {/* Desktop row. Fixed column widths are sized so the name column (1fr)
          keeps ~140px inside the page's 800px max width — widen any of them and
          the name silently collapses to nothing. */}
      <div className="hidden sm:grid sm:grid-cols-[minmax(0,1fr)_64px_84px_84px_92px_92px_104px_108px] sm:items-center sm:px-4 sm:py-3">
        {/* Tên */}
        <div className="flex items-center min-w-0 pr-3">
          <span className="text-sm font-semibold text-slate-900 truncate">{material.tenVatTu}</span>
        </div>

        {/* Nguồn */}
        <div className="flex items-center justify-center">
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
            isHtx ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {material.nguonGoc === 'HTX cấp/ứng' ? 'HTX' : 'Tự mua'}
          </span>
        </div>

        {/* Nhóm */}
        <div className="text-sm text-slate-700 text-center truncate">{material.nhomVatTu || '-'}</div>

        {/* Tồn kho */}
        <div className="text-sm text-slate-700 text-center">
          {material.tonKho ?? material.soLuong} {material.donVi}
        </div>

        {/* Ngày nhập */}
        <div className="text-sm text-slate-500 text-center">{formatDate(material.ngayCap)}</div>

        {/* Mùa vụ */}
        <div className="text-sm text-slate-500 text-center truncate" title={season?.name || '-'}>
          {season?.name || '-'}
        </div>

        {/* Trạng thái */}
        <div className="flex items-center justify-center">
          <StatusBadge status={material.status} />
        </div>

        {/* Thao tác */}
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onView}
            className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            title="Xem"
          >
            <i className="ri-eye-line" />
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="w-8 h-8 flex items-center justify-center rounded-md text-agrida-700 hover:text-agrida-800 hover:bg-agrida-50 transition-colors"
            title="Sửa"
          >
            <i className="ri-edit-line" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
              canDelete ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-slate-300 cursor-not-allowed'
            }`}
            title={canDelete ? 'Xóa' : 'Vật tư do HTX cấp/ứng không thể xóa'}
          >
            <i className="ri-delete-bin-line" />
          </button>
        </div>
      </div>

      {/* Mobile row */}
      <div className="sm:hidden flex flex-col gap-1.5 px-4 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{material.tenVatTu}</span>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
            isHtx ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-500'
          }`}>
            {material.nguonGoc === 'HTX cấp/ứng' ? 'HTX' : 'Tự mua'}
          </span>
          <StatusBadge status={material.status} />
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
            <span className="bg-slate-100 rounded px-1.5 py-0.5">{material.nhomVatTu || '-'}</span>
            <span>{material.tonKho ?? material.soLuong} {material.donVi}</span>
            <span>{formatDate(material.ngayCap)}</span>
            {season && <span>{season.name}</span>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onView}
              className="w-8 h-8 flex items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              title="Xem"
            >
              <i className="ri-eye-line" />
            </button>
            <button
              type="button"
              onClick={onEdit}
              className="w-8 h-8 flex items-center justify-center rounded-md text-agrida-700 hover:text-agrida-800 hover:bg-agrida-50 transition-colors"
              title="Sửa"
            >
              <i className="ri-edit-line" />
            </button>
            <button
              type="button"
              onClick={onDelete}
              disabled={!canDelete}
              className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                canDelete ? 'text-red-600 hover:text-red-700 hover:bg-red-50' : 'text-slate-300 cursor-not-allowed'
              }`}
              title={canDelete ? 'Xóa' : 'Vật tư do HTX cấp/ứng không thể xóa'}
            >
              <i className="ri-delete-bin-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}